import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import {
  ANEXO5_COLS,
  ANEXO5_MERGES,
  ANEXO5_ESTILOS,
  ANEXO5_CELDAS,
  EstiloA5,
} from './anexo5-template';
import {
  ANEXO6_COLS,
  ANEXO6_ESTILOS,
  ANEXO6_CELDAS,
  EstiloA6,
} from './anexo6-template';
import { CommonVariables } from '../../elements/funciones/funcionesGenerales';
import { COLOR_RANGO_INDICADORES_GLOBALES, COLOR_NIVEL_RIESGO } from './indicadores-globales-template';
import { ANEXO3_CUENTAS_OCULTAS } from './anexo3-cuentas-ocultas';
import { ANEXO4_CUENTAS_OCULTAS } from './anexo4-cuentas-ocultas';

export interface DatoAnexo3 {
  codigo: string;
  descripcion: string;
  nivel1?: number | string;
  nivel2?: number | string;
  nivel3?: number | string;
  nivel4?: number | string;
  nivel5?: number | string;
  nivel6?: number | string;
  nivel7?: number | string;
  nivel8?: number | string;
}

export interface DatoAnexo4 {
  codigo: string;
  descripcion: string;
  nivel1?: number | string;
  nivel2?: number | string;
  nivel3?: number | string;
  nivel4?: number | string;
  nivel5?: number | string;
  nivel6?: number | string;
  nivel7?: number | string;
}

export interface DatoAnexo6 {
  descripcion: string;
  cta?: number | string;
  total?: number | string;
  moneda_nacional?: number | string;
  cta_extranjera?: number | string;
  moneda_extranjera?: number | string;
}

/** Textos traducidos para los Anexos 3 y 4, vienen del componente (mismas claves de es.json que la vista previa). */
export interface TextosAnexoAnalitico {
  colCodigo: string;
  colDescripcion: string;
  cooperativaLabel: string;
  fechaInformacionLabel: string;
  tipoLabel: string;
  variosElementos: string;
  cifrasLempiras: string;
  nivelLabel: string;
  /** Etiquetas de los 4 bloques de firma, en el orden en que van en la hoja. */
  firmas: string[];
}

/** Igual que TextosAnexoAnalitico pero para los Anexos condensados (5 y 6). */
export interface TextosAnexoCondensado {
  cooperativaLabel: string;
  fechaInformacionLabel: string;
  cifrasLempiras: string;
  colCta: string;
  colTotal: string;
  colMonedaNacional: string;
  colMonedaExtranjera: string;
  firmas: string[];
}

export interface ConfigAnexo3 {
  titulo: string;
  subtitulo: string;
  cooperativa: string;
  fechaInformacion: string;
  datos: DatoAnexo3[];
  nombreArchivo: string;
  imagePath?: string;
  /** Segunda imagen opcional, se inserta junto a imagePath. Solo se usa para Chorotega (logos de gobierno). */
  imagePath2?: string;
  textos: TextosAnexoAnalitico;
}

export interface ConfigAnexo5 {
  titulo: string;
  subtitulo: string;
  cooperativa: string;
  fechaInformacion: string;
  valores: { [codigo: string]: number };
  nombreArchivo: string;
  imagePath?: string;
  imagePath2?: string;
  /** Solo hace falta la lista de firmas; el resto de los textos ya vienen en ANEXO5_CELDAS. */
  firmas: string[];
}

export interface ConfigAnexo6 {
  titulo: string;
  subtitulo: string;
  cooperativa: string;
  fechaInformacion: string;
  valores: { [codigo: string]: number };
  nombreArchivo: string;
  imagePath?: string;
  imagePath2?: string;
  textos: TextosAnexoCondensado;
}

/** Fila de la hoja "indicadores globales": nivel 1 = sección (ej. "1. LÍMITES DE RIESGO"), nivel 2 = sub-indicador. */
export interface DatoIndicadorGlobal {
  nivel: number;
  numero: string;
  descripcion: string;
  parametros?: string | number;
  resultado?: number | string;
  rango?: string;
  puntaje?: number | string;
  /** id del indicador en el catálogo, solo para emparejar con el detalle de evaluación (no se imprime). */
  id?: string;
}

/** Textos traducidos de la hoja "indicadores globales", mismas claves de es.json que la vista previa. */
export interface TextosIndicadoresGlobales {
  consejoNacional: string;
  superintendencia: string;
  tituloGlobales: string;
  cifrasAl: string;
  colIndicador: string;
  colParametros: string;
  colResultado: string;
  colRango: string;
  colPuntaje: string;
  sumatoriaBase: string;
  calificacionBase: string;
  nivelRiesgoLabel: string;
  elaboradoPor: string;
  autorizadoGerente: string;
}

export interface ConfigIndicadoresGlobales {
  cooperativa: string;
  fechaInformacion: string;
  datos: DatoIndicadorGlobal[];
  nombreArchivo: string;
  imagePath?: string;
  imagePath2?: string;
  textos: TextosIndicadoresGlobales;
}

/** Fila de la tabla principal (secciones I-VI) del Anexo A. 'titulo' son los
 * encabezados de sección, 'seccion' es un subencabezado dentro de la sección (ej.
 * "Total activo BG", pegado al dato de arriba, sin fila en blanco antes), 'destacado'
 * son subtotales (negrita), 'total' es el total general al final (negrita, en
 * mayúsculas, con una fila en blanco antes). */
export interface DatoActivoPonderado {
  codigo: string;
  descripcion: string;
  valor: number;
  tipo: 'titulo' | 'seccion' | 'destacado' | 'total' | 'dato';
  /** Negrita solo en la columna de código (ej. "1 Resto de Activos"), sin afectar la
   * descripción ni el valor. Distinto del negrita "sin código" que afecta toda la etiqueta. */
  codigoBold?: boolean;
}

/** Fila del bloque resumen "Total de Activos Ponderados por Riesgos" (1 por tramo de riesgo). */
export interface FilaResumenActivoPonderado {
  numero: number;
  descripcion: string;
  suma: number;
  ponderado: number;
}

/** Fila del bloque "Capital Institucional Ajustado", al lado del resumen. */
export interface FilaCapitalInstitucional {
  descripcion: string;
  valor: number;
  porcentaje?: boolean;
}

/** Textos traducidos del Anexo A, mismas claves de es.json que la vista previa. */
export interface TextosActivosPonderados {
  titulo: string;
  resumenTitulo: string;
  colSumaActivos: string;
  colActivosPonderados: string;
  totalesLabel: string;
  capitalInstitucionalTitulo: string;
  elaboradoPor: string;
  autorizadoPor: string;
}

export interface ConfigActivosPonderados {
  datos: DatoActivoPonderado[];
  resumenPonderados: FilaResumenActivoPonderado[];
  totalResumenSuma: number;
  totalResumenPonderado: number;
  capitalInstitucional: FilaCapitalInstitucional[];
  nombreArchivo: string;
  imagePath?: string;
  imagePath2?: string;
  textos: TextosActivosPonderados;
}

@Injectable({
  providedIn: 'root'
})
export class NormativosExcelService {

  private readonly FUENTE_BASE: Partial<ExcelJS.Font> = { name: 'Calibri', size: 14, color: { theme: 1 } };
  private readonly FUENTE_BASE_BOLD: Partial<ExcelJS.Font> = { name: 'Calibri', size: 14, bold: true, color: { theme: 1 } };
  private readonly FUENTE_A4: Partial<ExcelJS.Font> = { name: 'Calibri', size: 11, color: { theme: 1 } };
  private readonly FUENTE_A4_BOLD: Partial<ExcelJS.Font> = { name: 'Calibri', size: 11, bold: true, color: { theme: 1 } };
  private readonly COLOR_BORDE = { argb: 'FF3F3F3F' };

  constructor(private httpClient: HttpClient, private translate: TranslateService) { }

  private createExcel(nombreHoja: string): ExcelJS.Workbook {
    let libroExcel = new ExcelJS.Workbook();
    libroExcel.addWorksheet(nombreHoja, {});
    return libroExcel;
  }

  private printExcel(nombre: string, libroExcel: ExcelJS.Workbook) {
    libroExcel.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      FileSaver.saveAs(blob, nombre);
    });
  }

  private parseFecha(fecha: string): Date {
    if (!fecha) return new Date();
    const partes = fecha.split('/');
    if (partes.length === 3 && partes[2].length === 4) {
      return new Date(Date.UTC(Number(partes[2]), Number(partes[1]) - 1, Number(partes[0])));
    }
    const d = new Date(fecha);
    return isNaN(d.getTime()) ? new Date() : d;
  }

  /** Calcula el alto de fila (en puntos) que necesita un texto largo para no cortarse al hacer wrap. */
  private calcularAlturaFila(texto: string, anchoColumna: number, tamanoFuente: number, alturaLinea = 18): number {
    if (!texto) return alturaLinea;
    const caracteresPorLinea = Math.max(8, Math.floor((anchoColumna - 2) * (11 / tamanoFuente) * 0.85));
    const lineas = Math.max(1, Math.ceil(texto.length / caracteresPorLinea));
    return lineas * alturaLinea;
  }

  /** Estilo de un banner de sección fusionado (gris, negrita, centrado). El relleno se
   * pone en las 3 celdas del merge, no solo en la maestra: algunos visores (ej.
   * LibreOffice) no siempre completan el relleno de toda el área fusionada si las
   * celdas de atrás quedan sin relleno propio una vez que aplicarBordeFila les toca
   * el borde individualmente. */
  private aplicarBannerSeccion(hoja: ExcelJS.Worksheet, fila: number, nCols: number): void {
    const row = hoja.getRow(fila);
    for (let c = 1; c <= nCols; c++) {
      const cell = row.getCell(c);
      cell.font = this.FUENTE_A4_BOLD;
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } };
    }
    this.aplicarBordeFila(hoja, fila, nCols);
  }

  private aplicarBordeFila(hoja: ExcelJS.Worksheet, fila: number, nCols: number): void {
    const row = hoja.getRow(fila);
    for (let c = 1; c <= nCols; c++) {
      const cell = row.getCell(c);
      cell.border = {
        top: { style: 'thin', color: this.COLOR_BORDE },
        bottom: { style: 'thin', color: this.COLOR_BORDE },
        ...(c === 1 ? { left: { style: 'thin', color: this.COLOR_BORDE } } : {}),
        ...(c === nCols ? { right: { style: 'thin', color: this.COLOR_BORDE } } : {}),
      };
    }
  }

  private async cargarImagen(imagePath: string): Promise<ArrayBuffer | null> {
    try {
      const response = await fetch(imagePath);
      if (!response.ok) {
        return null;
      }
      return await response.arrayBuffer();
    } catch {
      return null;
    }
  }

  // Proporción ancho/alto de los 2 logos de gobierno (solo Chorotega).
  private readonly LOGO_GOBIERNO_RATIO = 350 / 106;
  private readonly LOGO_CONSEJO_RATIO = 211 / 77;

  /** Ancho en píxeles de una columna, con la fórmula real de Excel (Calibri 11). */
  private anchoColumnaAPixeles(anchoUnidades: number): number {
    const mdw = 7;
    return Math.floor(((256 * anchoUnidades + Math.floor(128 / mdw)) / 256) * mdw);
  }

  /** Convierte un desplazamiento en píxeles a columna + offset en EMU (nativeCol/nativeColOff).
   * No se puede usar el `tl.col` fraccionario normal de ExcelJS: internamente usa otra escala
   * (ancho de columna * 10000) que no coincide con los EMU reales de `ext.width/height`, y eso
   * dejaba la segunda imagen más pegada de lo calculado. */
  private posicionColConDesplazamientoPx(
    hoja: ExcelJS.Worksheet,
    colInicio: number,
    offsetPx: number,
  ): { nativeCol: number; nativeColOff: number } {
    const EMU_POR_PIXEL = 9525;
    // colInicio puede venir fraccionario (ej. 3.4 = "casi a la mitad" de la columna D);
    // la parte decimal se convierte a píxeles y se suma al desplazamiento pedido.
    let col = Math.floor(colInicio);
    const anchoColInicio = this.anchoColumnaAPixeles(hoja.getColumn(col + 1).width || 8.43);
    let restante = offsetPx + (colInicio - col) * anchoColInicio;
    for (let i = 0; i < 50; i++) {
      const anchoCol = hoja.getColumn(col + 1).width || 8.43;
      const anchoPx = this.anchoColumnaAPixeles(anchoCol);
      if (restante < anchoPx) {
        return { nativeCol: col, nativeColOff: Math.round(restante * EMU_POR_PIXEL) };
      }
      restante -= anchoPx;
      col++;
    }
    return { nativeCol: col, nativeColOff: 0 };
  }

  /** Misma fila en formato nativeRow/nativeRowOff, para usarla junto con nativeCol/nativeColOff. */
  private posicionFilaNativa(hoja: ExcelJS.Worksheet, row: number): { nativeRow: number; nativeRowOff: number } {
    const nativeRow = Math.floor(row);
    const alturaFila = hoja.getRow(nativeRow + 1).height || 18;
    const nativeRowOff = Math.floor((row - nativeRow) * alturaFila * 10000);
    return { nativeRow, nativeRowOff };
  }

  /** Inserta el logo del encabezado. Sin imagePath2 es una sola imagen; con imagePath2
   * inserta las 2 lado a lado (solo se usa hoy para Chorotega). */
  private async insertarLogo(
    libroExcel: ExcelJS.Workbook,
    hoja: ExcelJS.Worksheet,
    imagePath: string | undefined,
    imagePath2: string | undefined,
    tl: { col: number; row: number },
    extUnico: { width: number; height: number },
  ): Promise<void> {
    if (!imagePath) return;
    try {
      const buffer1 = await this.cargarImagen(imagePath);
      if (!buffer1) return;
      const imageId1 = libroExcel.addImage({ buffer: buffer1, extension: 'png' });

      if (!imagePath2) {
        hoja.addImage(imageId1, { tl, ext: extUnico, editAs: 'oneCell' });
        return;
      }

      const buffer2 = await this.cargarImagen(imagePath2);
      if (!buffer2) {
        hoja.addImage(imageId1, { tl, ext: extUnico, editAs: 'oneCell' });
        return;
      }
      const imageId2 = libroExcel.addImage({ buffer: buffer2, extension: 'png' });

      const altura = extUnico.height;
      const ancho1 = Math.round(altura * this.LOGO_GOBIERNO_RATIO);
      const ancho2 = Math.round(altura * this.LOGO_CONSEJO_RATIO);
      const margen = 10;
      const { nativeRow, nativeRowOff } = this.posicionFilaNativa(hoja, tl.row);

      // Las 2 imágenes usan la misma conversión a EMU, para que el offset fraccionario
      // de tl.col (ej. "a mitad de columna") quede igual en ambas.
      const pos1 = this.posicionColConDesplazamientoPx(hoja, tl.col, 0);
      hoja.addImage(imageId1, {
        tl: { nativeCol: pos1.nativeCol, nativeColOff: pos1.nativeColOff, nativeRow, nativeRowOff } as any,
        ext: { width: ancho1, height: altura },
        editAs: 'oneCell',
      });
      const pos2 = this.posicionColConDesplazamientoPx(hoja, tl.col, ancho1 + margen);
      hoja.addImage(imageId2, {
        tl: { nativeCol: pos2.nativeCol, nativeColOff: pos2.nativeColOff, nativeRow, nativeRowOff } as any,
        ext: { width: ancho2, height: altura },
        editAs: 'oneCell',
      });
    } catch {
    }
  }

  /** Layout de logos de "Indicadores Globales": Gobierno más grande a la izquierda,
   * Consejo Nacional separado cerca del borde derecho (no pegado como en los otros
   * anexos). Si no caben los 2 tamaños ideales, se achican proporcionalmente.
   * @returns el alto (px) del logo más alto, para reservar espacio antes del encabezado. */
  private async insertarLogosIndicadoresGlobales(
    libroExcel: ExcelJS.Workbook,
    hoja: ExcelJS.Worksheet,
    imagePath: string | undefined,
    imagePath2: string | undefined,
  ): Promise<number> {
    if (!imagePath) return 0;
    const tl = { col: 0, row: 5 };
    try {
      const buffer1 = await this.cargarImagen(imagePath);
      if (!buffer1) return 0;
      const imageId1 = libroExcel.addImage({ buffer: buffer1, extension: 'png' });

      if (!imagePath2) {
        hoja.addImage(imageId1, { tl, ext: { width: 220, height: 47 }, editAs: 'oneCell' });
        return 47;
      }

      const buffer2 = await this.cargarImagen(imagePath2);
      if (!buffer2) {
        hoja.addImage(imageId1, { tl, ext: { width: 220, height: 47 }, editAs: 'oneCell' });
        return 47;
      }
      const imageId2 = libroExcel.addImage({ buffer: buffer2, extension: 'png' });

      let anchoHojaPx = 0;
      for (let c = 1; c <= 5; c++) anchoHojaPx += this.anchoColumnaAPixeles(hoja.getColumn(c).width || 8.43);

      let alturaGobierno = 65;
      let alturaConsejo = 55;
      let anchoGobierno = Math.round(alturaGobierno * this.LOGO_GOBIERNO_RATIO);
      let anchoConsejo = Math.round(alturaConsejo * this.LOGO_CONSEJO_RATIO);
      const margenDerecho = 15;
      const margenMinimoEntreLogos = 40;

      let offsetConsejo = anchoHojaPx - anchoConsejo - margenDerecho;
      if (offsetConsejo - anchoGobierno < margenMinimoEntreLogos) {
        // La hoja no alcanza para los 2 tamaños "ideales" con separación
        // mínima entre ambos: se achican proporcionalmente para que quepan.
        const disponible = anchoHojaPx - margenDerecho - margenMinimoEntreLogos;
        const factor = disponible / (anchoGobierno + anchoConsejo);
        anchoGobierno = Math.max(30, Math.round(anchoGobierno * factor));
        anchoConsejo = Math.max(30, Math.round(anchoConsejo * factor));
        alturaGobierno = Math.round(anchoGobierno / this.LOGO_GOBIERNO_RATIO);
        alturaConsejo = Math.round(anchoConsejo / this.LOGO_CONSEJO_RATIO);
        offsetConsejo = anchoHojaPx - anchoConsejo - margenDerecho;
      }

      hoja.addImage(imageId1, { tl, ext: { width: anchoGobierno, height: alturaGobierno }, editAs: 'oneCell' });
      const { nativeCol, nativeColOff } = this.posicionColConDesplazamientoPx(hoja, tl.col, offsetConsejo);
      const { nativeRow, nativeRowOff } = this.posicionFilaNativa(hoja, tl.row);
      hoja.addImage(imageId2, {
        tl: { nativeCol, nativeColOff, nativeRow, nativeRowOff } as any,
        ext: { width: anchoConsejo, height: alturaConsejo },
        editAs: 'oneCell',
      });
      return Math.max(alturaGobierno, alturaConsejo);
    } catch {
      return 0;
    }
  }

  public async generateAnexo3(config: ConfigAnexo3): Promise<void> {
    const { titulo, subtitulo, cooperativa, fechaInformacion, datos, nombreArchivo, imagePath, imagePath2, textos } = config;

    const nombreHoja = 'Anexo3_BG';
    const numColumnas = 10;

    const libroExcel = this.createExcel(nombreHoja);
    const hojaTrabajo = libroExcel.getWorksheet(nombreHoja);

    if (!hojaTrabajo) {
      throw new Error(this.translate.instant('reportes-normativos.error-hoja-anexo3'));
    }

    // Abre el archivo en vista previa de salto de página, para ver de una vez cuántas hojas ocupa.
    hojaTrabajo.views = [{ showGridLines: false, style: 'pageBreakPreview' }];
    hojaTrabajo.properties.defaultRowHeight = 18;
    hojaTrabajo.pageSetup = {
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      showGridLines: false,
      margins: { left: 0.236, right: 0.236, top: 0.748, bottom: 0.748, header: 0, footer: 0 },
    };

    hojaTrabajo.columns = [
      { width: 24.88 }, { width: 43.13 }, { width: 17.25 }, { width: 22.63 },
      { width: 15.38 }, { width: 17.25 }, { width: 16.5 }, { width: 12.88 },
      { width: 1.75 }, { width: 20.13 },
    ];

    hojaTrabajo.addRow([]);

    const row2 = hojaTrabajo.addRow([titulo]);
    hojaTrabajo.mergeCells('A2:J2');
    const cell2 = row2.getCell(1);
    cell2.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    cell2.font = this.FUENTE_BASE_BOLD;
    cell2.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE7E6E6' },
    };
    this.aplicarBordeFila(hojaTrabajo, 2, 10);

    hojaTrabajo.addRow([]);

    const row4 = hojaTrabajo.addRow([subtitulo]);
    hojaTrabajo.mergeCells('A4:J4');
    const cell4 = row4.getCell(1);
    cell4.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    cell4.font = this.FUENTE_BASE_BOLD;
    this.aplicarBordeFila(hojaTrabajo, 4, 10);

    hojaTrabajo.addRow([]);
    hojaTrabajo.addRow([]);

    const row7 = hojaTrabajo.addRow([textos.cooperativaLabel]);
    row7.getCell(1).font = this.FUENTE_BASE_BOLD;
    row7.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    hojaTrabajo.mergeCells(row7.number, 1, row7.number, 3);
    const celdaCoop = row7.getCell(4);
    celdaCoop.value = cooperativa;
    celdaCoop.font = this.FUENTE_BASE;
    celdaCoop.alignment = { horizontal: 'left', vertical: 'middle' };
    celdaCoop.border = {
      top: { style: 'thin', color: this.COLOR_BORDE },
      bottom: { style: 'thin', color: this.COLOR_BORDE },
    };
    hojaTrabajo.mergeCells(row7.number, 4, row7.number, numColumnas);

    hojaTrabajo.addRow([]);
    hojaTrabajo.addRow([]);

    const row10 = hojaTrabajo.addRow([textos.fechaInformacionLabel]);
    row10.getCell(1).font = this.FUENTE_BASE_BOLD;
    row10.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    hojaTrabajo.mergeCells(row10.number, 1, row10.number, 3);
    const celdaFecha = row10.getCell(4);
    celdaFecha.value = this.parseFecha(fechaInformacion);
    celdaFecha.numFmt = 'dd/mmmm/yyyy';
    celdaFecha.font = this.FUENTE_BASE;
    celdaFecha.alignment = { horizontal: 'left', vertical: 'middle' };
    celdaFecha.border = {
      top: { style: 'thin', color: this.COLOR_BORDE },
      bottom: { style: 'thin', color: this.COLOR_BORDE },
    };
    hojaTrabajo.mergeCells(row10.number, 4, row10.number, numColumnas);

    const row11 = hojaTrabajo.addRow([textos.tipoLabel, textos.variosElementos]);
    row11.getCell(1).font = this.FUENTE_BASE;
    row11.getCell(1).alignment = { shrinkToFit: true };
    row11.getCell(2).font = this.FUENTE_BASE;
    row11.getCell(2).alignment = { shrinkToFit: true };

    const row13 = hojaTrabajo.addRow([textos.cifrasLempiras, '', textos.nivelLabel]);
    row13.getCell(1).font = this.FUENTE_BASE;
    row13.getCell(1).alignment = { shrinkToFit: true };
    row13.getCell(3).font = this.FUENTE_BASE;
    row13.getCell(3).alignment = { shrinkToFit: true };
    for (let c = 1; c <= numColumnas; c++) {
      row13.getCell(c).border = {
        top: { style: 'thin', color: this.COLOR_BORDE },
        left: { style: 'thin', color: this.COLOR_BORDE },
        right: { style: 'thin', color: this.COLOR_BORDE },
      };
    }

    const headerRow = hojaTrabajo.addRow([textos.colCodigo, textos.colDescripcion, 1, 2, 3, 4, 5, 6, 7, 8]);
    headerRow.eachCell((cell: ExcelJS.Cell, colNum: number) => {
      if (colNum <= numColumnas) {
        cell.font = this.FUENTE_BASE_BOLD;
        cell.alignment = { wrapText: true, vertical: 'middle' };
        cell.border = {
          bottom: { style: 'thin', color: this.COLOR_BORDE },
          left: { style: 'thin', color: this.COLOR_BORDE },
          right: { style: 'thin', color: this.COLOR_BORDE },
        };
      }
    });

    for (const dato of datos) {
      const dataRow = hojaTrabajo.addRow([
        dato.codigo,
        dato.descripcion,
        dato.nivel1 ?? '',
        dato.nivel2 ?? '',
        dato.nivel3 ?? '',
        dato.nivel4 ?? '',
        dato.nivel5 ?? '',
        dato.nivel6 ?? '',
        dato.nivel7 ?? '',
        dato.nivel8 ?? '',
      ]);

      dataRow.eachCell((cell: ExcelJS.Cell, colNum: number) => {
        if (colNum <= numColumnas) {
          cell.font = (colNum === 1 || colNum === 2) ? this.FUENTE_BASE_BOLD : this.FUENTE_BASE;
          cell.alignment = { shrinkToFit: true, vertical: 'middle' };
          if (typeof cell.value === 'number') {
            cell.numFmt = '#,##0.00';
          }
        }
      });

      if (ANEXO3_CUENTAS_OCULTAS.has(dato.codigo)) {
        dataRow.hidden = true;
      }
    }

    // Firmas como imagen (recorte real de la plantilla oficial), en vez de las líneas de
    // texto. Se deja comentado el modo anterior por si se necesita volver a él.
    // this.agregarSeccionFirmas(hojaTrabajo, numColumnas, textos.firmas);
    await this.insertarFirmasImagen(libroExcel, hojaTrabajo, numColumnas, '/assets/img/firma-anexos.png');

    await this.insertarLogo(libroExcel, hojaTrabajo, imagePath, imagePath2, { col: 0, row: 4.5 }, { width: 300, height: 64 });

    hojaTrabajo.pageSetup.printArea = `A1:J${hojaTrabajo.rowCount}`;

    this.printExcel(nombreArchivo, libroExcel);
  }

  /** Reparte columnas en bloques contiguos por ancho real (no por cantidad), para que
   * las firmas queden parejas. Prueba todas las particiones posibles y usa la más pareja. */
  private repartirColumnasPorAncho(anchos: number[], grupos: number): { c1: number; c2: number }[] {
    const n = anchos.length;
    const total = anchos.reduce((a, b) => a + b, 0);
    const objetivo = total / grupos;
    const prefijo: number[] = [0];
    for (const a of anchos) prefijo.push(prefijo[prefijo.length - 1] + a);

    const posiblesCortes = Array.from({ length: n - 1 }, (_, i) => i + 1);
    const combinaciones = (arr: number[], k: number): number[][] => {
      if (k === 0) return [[]];
      const resultado: number[][] = [];
      for (let i = 0; i <= arr.length - k; i++) {
        for (const resto of combinaciones(arr.slice(i + 1), k - 1)) {
          resultado.push([arr[i], ...resto]);
        }
      }
      return resultado;
    };

    let mejorCortes: number[] = [];
    let mejorPuntaje = Infinity;
    for (const cortes of combinaciones(posiblesCortes, grupos - 1)) {
      const bordes = [0, ...cortes, n];
      let puntaje = 0;
      for (let i = 0; i < grupos; i++) {
        const ancho = prefijo[bordes[i + 1]] - prefijo[bordes[i]];
        puntaje += (ancho - objetivo) ** 2;
      }
      if (puntaje < mejorPuntaje) {
        mejorPuntaje = puntaje;
        mejorCortes = cortes;
      }
    }

    const bordesFinales = [0, ...mejorCortes, n];
    const bloques: { c1: number; c2: number }[] = [];
    for (let i = 0; i < grupos; i++) {
      bloques.push({ c1: bordesFinales[i] + 1, c2: bordesFinales[i + 1] });
    }
    return bloques;
  }

  private agregarSeccionFirmas(hoja: ExcelJS.Worksheet, numColumnas: number, firmas: string[]): void {
    hoja.addRow([]);
    hoja.addRow([]);
    hoja.addRow([]);

    const nFirmas = firmas.length;
    const anchos: number[] = [];
    for (let c = 1; c <= numColumnas; c++) {
      anchos.push(hoja.getColumn(c).width || 10);
    }
    const bloques = this.repartirColumnasPorAncho(anchos, nFirmas);

    const filaLinea = hoja.addRow([]);
    const filaEtiquetas = hoja.addRow([]);
    filaLinea.height = 22; // un poco más alta para que el guion bajo no quede pegado al nombre de abajo

    let alturaMax = 30;
    firmas.forEach((label, i) => {
      const { c1, c2 } = bloques[i];

      let anchoBloque = 0;
      for (let c = c1; c <= c2; c++) {
        anchoBloque += hoja.getColumn(c).width || 10;
      }

      // La línea de firma es texto con guiones bajos (no borde), con largo proporcional
      // al texto pero topado al ancho del bloque para que no se salga de la tabla.
      const deseadoPorTexto = Math.max(10, Math.round(label.length * 1.5));
      const maxPorAncho = Math.max(5, Math.floor(anchoBloque * 0.9));
      const cantidadGuiones = Math.min(deseadoPorTexto, maxPorAncho);
      hoja.mergeCells(filaLinea.number, c1, filaLinea.number, c2);
      const celdaLinea = hoja.getCell(filaLinea.number, c1);
      celdaLinea.value = '_'.repeat(cantidadGuiones);
      celdaLinea.font = this.FUENTE_BASE;
      celdaLinea.alignment = { horizontal: 'center', vertical: 'middle' };

      hoja.mergeCells(filaEtiquetas.number, c1, filaEtiquetas.number, c2);
      const celdaTexto = hoja.getCell(filaEtiquetas.number, c1);
      celdaTexto.value = label;
      celdaTexto.font = this.FUENTE_BASE_BOLD;
      celdaTexto.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

      const altura = this.calcularAlturaFila(label, anchoBloque, 14);
      if (altura > alturaMax) alturaMax = altura;
    });
    filaEtiquetas.height = alturaMax;
  }

  /** Inserta la sección de firmas como imagen (recorte real de la plantilla oficial), en
   * vez de las líneas de texto que arma agregarSeccionFirmas. Usado en los anexos 3, 4,
   * 5 y 6; Indicadores Globales y Activos Ponderados siguen con su propio formato. */
  private async insertarFirmasImagen(
    libroExcel: ExcelJS.Workbook,
    hoja: ExcelJS.Worksheet,
    numColumnas: number,
    imagePath: string,
  ): Promise<void> {
    const buffer = await this.cargarImagen(imagePath);
    if (!buffer) return;
    const imageId = libroExcel.addImage({ buffer, extension: 'png' });

    hoja.addRow([]);
    hoja.addRow([]);

    // Proporción ancho/alto real de la captura ("firma-anexos.png", tomada de la
    // plantilla oficial): 987x119px.
    const RATIO_FIRMA = 987 / 119;
    let anchoTotalPx = 0;
    for (let c = 1; c <= numColumnas; c++) {
      anchoTotalPx += this.anchoColumnaAPixeles(hoja.getColumn(c).width || 8.43);
    }
    const anchoPx = anchoTotalPx;
    const altoPx = anchoPx / RATIO_FIRMA;

    const { nativeRow, nativeRowOff } = this.posicionFilaNativa(hoja, hoja.rowCount);
    hoja.addImage(imageId, {
      tl: { nativeCol: 0, nativeColOff: 0, nativeRow, nativeRowOff } as any,
      ext: { width: anchoPx, height: altoPx },
      editAs: 'oneCell',
    });

    // Reserva suficientes filas en blanco para el alto real de la imagen (1pt = 96/72 px).
    const alturaFilaPt = hoja.properties.defaultRowHeight || 18;
    const altoPt = altoPx / (96 / 72);
    const filasNecesarias = Math.ceil(altoPt / alturaFilaPt);
    for (let i = 0; i < filasNecesarias; i++) hoja.addRow([]);
  }

  /* ===== ANEXO 4 — ESTADO DE EXCEDENTES O PÉRDIDAS ANALÍTICO ===== */

  public async generateAnexo4(config: {
    titulo: string;
    subtitulo: string;
    cooperativa: string;
    fechaInformacion: string;
    datos: DatoAnexo4[];
    nombreArchivo: string;
    imagePath?: string;
    imagePath2?: string;
    textos: TextosAnexoAnalitico;
  }): Promise<void> {
    const { titulo, subtitulo, cooperativa, fechaInformacion, datos, nombreArchivo, imagePath, imagePath2, textos } = config;

    const nombreHoja = 'Anexo4_ER';
    const libroExcel = this.createExcel(nombreHoja);
    const hoja = libroExcel.getWorksheet(nombreHoja);
    if (!hoja) {
      throw new Error(this.translate.instant('reportes-normativos.error-hoja-anexo4'));
    }

    const numColumnas = 9;

    // Abre el archivo en vista previa de salto de página, para ver de una vez cuántas hojas ocupa.
    hoja.views = [{ showGridLines: false, style: 'pageBreakPreview' }];
    hoja.properties.defaultRowHeight = 18;
    hoja.pageSetup = {
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      showGridLines: false,
      margins: { left: 0.236, right: 0.236, top: 0.748, bottom: 1.5, header: 0, footer: 0 },
    };

    hoja.columns = [
      { width: 14 }, { width: 37.38 }, { width: 11.13 }, { width: 13.63 },
      { width: 14.25 }, { width: 12.88 }, { width: 13.88 }, { width: 12.63 }, { width: 12.75 },
    ];

    hoja.addRow([]);

    const row2 = hoja.addRow([titulo]);
    hoja.mergeCells('A2:I2');
    const cell2 = row2.getCell(1);
    cell2.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    cell2.font = this.FUENTE_A4_BOLD;
    cell2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7E6E6' } };
    this.aplicarBordeFila(hoja, 2, numColumnas);

    hoja.addRow([]);

    const row4 = hoja.addRow([subtitulo]);
    hoja.mergeCells('A4:I4');
    const cell4 = row4.getCell(1);
    cell4.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    cell4.font = this.FUENTE_A4_BOLD;
    this.aplicarBordeFila(hoja, 4, numColumnas);

    hoja.addRow([]);
    hoja.addRow([]);

    const row7 = hoja.addRow([textos.cooperativaLabel]);
    row7.getCell(1).font = this.FUENTE_A4_BOLD;
    row7.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    hoja.mergeCells(row7.number, 1, row7.number, 3);
    const celdaCoop = row7.getCell(4);
    celdaCoop.value = cooperativa;
    celdaCoop.font = this.FUENTE_A4;
    celdaCoop.alignment = { horizontal: 'left', vertical: 'middle' };
    celdaCoop.border = {
      top: { style: 'thin', color: this.COLOR_BORDE },
      bottom: { style: 'thin', color: this.COLOR_BORDE },
    };
    hoja.mergeCells(row7.number, 4, row7.number, numColumnas);

    hoja.addRow([]);
    hoja.addRow([]);

    const row10 = hoja.addRow([textos.fechaInformacionLabel]);
    row10.getCell(1).font = this.FUENTE_A4_BOLD;
    row10.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    hoja.mergeCells(row10.number, 1, row10.number, 3);
    const celdaFecha = row10.getCell(4);
    celdaFecha.value = this.parseFecha(fechaInformacion);
    celdaFecha.numFmt = 'dd/mmmm/yyyy';
    celdaFecha.font = this.FUENTE_A4;
    celdaFecha.alignment = { horizontal: 'left', vertical: 'middle' };
    celdaFecha.border = {
      top: { style: 'thin', color: this.COLOR_BORDE },
      bottom: { style: 'thin', color: this.COLOR_BORDE },
    };
    hoja.mergeCells(row10.number, 4, row10.number, numColumnas);

    const row11 = hoja.addRow([textos.tipoLabel, textos.variosElementos]);
    row11.getCell(1).font = this.FUENTE_A4;
    row11.getCell(1).alignment = { shrinkToFit: true };
    row11.getCell(2).font = this.FUENTE_A4;
    row11.getCell(2).alignment = { shrinkToFit: true };

    const row13 = hoja.addRow([textos.cifrasLempiras, '', textos.nivelLabel]);
    row13.getCell(1).font = this.FUENTE_A4;
    row13.getCell(1).alignment = { shrinkToFit: true };
    row13.getCell(3).font = this.FUENTE_A4;
    row13.getCell(3).alignment = { shrinkToFit: true };
    for (let c = 1; c <= numColumnas; c++) {
      row13.getCell(c).border = {
        top: { style: 'thin', color: this.COLOR_BORDE },
        left: { style: 'thin', color: this.COLOR_BORDE },
        right: { style: 'thin', color: this.COLOR_BORDE },
      };
    }

    const headerRow = hoja.addRow([textos.colCodigo, textos.colDescripcion, 1, 2, 3, 4, 5, 6, 7]);
    headerRow.eachCell((cell: ExcelJS.Cell, colNum: number) => {
      if (colNum <= numColumnas) {
        cell.font = this.FUENTE_A4_BOLD;
        cell.alignment = { wrapText: true, vertical: 'middle' };
        cell.border = {
          bottom: { style: 'thin', color: this.COLOR_BORDE },
          left: { style: 'thin', color: this.COLOR_BORDE },
          right: { style: 'thin', color: this.COLOR_BORDE },
        };
      }
    });

    for (const dato of datos) {
      const dataRow = hoja.addRow([
        dato.codigo,
        dato.descripcion,
        dato.nivel1 ?? '',
        dato.nivel2 ?? '',
        dato.nivel3 ?? '',
        dato.nivel4 ?? '',
        dato.nivel5 ?? '',
        dato.nivel6 ?? '',
        dato.nivel7 ?? '',
      ]);
      dataRow.eachCell((cell: ExcelJS.Cell, colNum: number) => {
        if (colNum <= numColumnas) {
          cell.font = (colNum === 1 || colNum === 2) ? this.FUENTE_A4_BOLD : this.FUENTE_A4;
          cell.alignment = { shrinkToFit: true, vertical: 'middle' };
          if (typeof cell.value === 'number') {
            cell.numFmt = '#,##0.00';
          }
        }
      });

      if (ANEXO4_CUENTAS_OCULTAS.has(dato.codigo)) {
        dataRow.hidden = true;
      }
    }

    // Firmas como imagen (recorte real de la plantilla oficial), en vez de las líneas de
    // texto. Se deja comentado el modo anterior por si se necesita volver a él.
    // this.agregarSeccionFirmas(hoja, numColumnas, textos.firmas);
    await this.insertarFirmasImagen(libroExcel, hoja, numColumnas, '/assets/img/firma-anexos.png');

    // El logo puede montarse verticalmente sobre las filas de COOPERATIVA/FECHA sin
    // problema: ese rótulo va alineado a la derecha de la columna C, bien separado de
    // las columnas A+B. Lo que sí hay que respetar es el ANCHO total (<= A+B ≈ 360px)
    // para no invadir la columna C donde empieza ese texto.
    await this.insertarLogo(libroExcel, hoja, imagePath, imagePath2, { col: 0, row: 4.3 }, { width: 340, height: 55 });

    hoja.pageSetup.printArea = `A1:I${hoja.rowCount}`;

    this.printExcel(nombreArchivo, libroExcel);
  }

  /* ===== ANEXO 5 — BALANCE GENERAL CONDENSADO (formato fijo) ===== */

  public async generateAnexo5(config: ConfigAnexo5): Promise<void> {
    const { titulo, subtitulo, cooperativa, fechaInformacion, valores, nombreArchivo, imagePath, imagePath2, firmas } = config;

    const libroExcel = this.createExcel('Anexo5_BG');
    const hoja = libroExcel.getWorksheet('Anexo5_BG');
    if (!hoja) {
      throw new Error(this.translate.instant('reportes-normativos.error-hoja-anexo5'));
    }

    // Abre el archivo en vista previa de salto de página, para ver de una vez cuántas hojas ocupa.
    hoja.views = [{ showGridLines: false, style: 'pageBreakPreview' }];
    hoja.properties.defaultRowHeight = 21;
    hoja.pageSetup = {
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      showGridLines: false,
      margins: { left: 0.236, right: 0.236, top: 0.748, bottom: 0.748, header: 0, footer: 0 },
    };

    Object.entries(ANEXO5_COLS).forEach(([col, ancho]) => {
      hoja.getColumn(col).width = ancho;
    });

    await this.insertarLogo(libroExcel, hoja, imagePath, imagePath2, { col: 0, row: 4.5 }, { width: 300, height: 64 });

    // Código de cuenta por fila (B = activo, H = pasivo): 3 dígitos = nivel 1 (negrita), 6 = nivel 2 (sangría).
    const codigoPorFila = new Map<number, { activo?: string; pasivo?: string }>();
    for (const celda of ANEXO5_CELDAS) {
      if (celda.k !== 'v' || !celda.v) continue;
      const m = celda.ref.match(/^([A-L]+)(\d+)$/);
      if (!m) continue;
      const [, col, filaStr] = m;
      const fila = Number(filaStr);
      if (col === 'B') {
        codigoPorFila.set(fila, { ...codigoPorFila.get(fila), activo: celda.v });
      } else if (col === 'H') {
        codigoPorFila.set(fila, { ...codigoPorFila.get(fila), pasivo: celda.v });
      }
    }
    const nivelDeCodigo = (codigo?: string): number => {
      const len = (codigo ?? '').trim().length;
      if (len === 3) return 1;
      if (len === 6) return 2;
      return 0;
    };

    for (const celda of ANEXO5_CELDAS) {
      const cell = hoja.getCell(celda.ref);

      if (celda.ph === 'titulo') {
        cell.value = titulo;
      } else if (celda.ph === 'subtitulo') {
        cell.value = subtitulo;
      } else if (celda.ph === 'cooperativa') {
        cell.value = cooperativa;
      } else if (celda.ph === 'fecha') {
        cell.value = this.parseFecha(fechaInformacion);
      } else if (celda.k === 'f') {
        cell.value = { formula: (celda.f || '').replace(/^=/, '') } as any;
      } else if (celda.k === 'lk') {
        // Sin código de cuenta (ej. celda de relleno en "PATRIMONIO") se deja en blanco, no en 0.
        if (celda.code) {
          const monto = valores[celda.code] ?? 0;
          cell.value = monto * (celda.sign ?? 1);
        }
      } else {
        cell.value = celda.v ?? '';
      }

      this.aplicarEstiloA5(cell, ANEXO5_ESTILOS[celda.s]);

      const refMatch = celda.ref.match(/^([A-L]+)(\d+)$/);
      if (refMatch && (refMatch[1] === 'A' || refMatch[1] === 'G')) {
        const fila = Number(refMatch[2]);
        const codigo = refMatch[1] === 'A' ? codigoPorFila.get(fila)?.activo : codigoPorFila.get(fila)?.pasivo;
        const nivel = nivelDeCodigo(codigo);
        if (nivel === 1) {
          cell.font = { ...cell.font, bold: true };
        } else if (nivel === 2) {
          cell.alignment = { ...cell.alignment, indent: 1 };
        }
      }
    }

    ANEXO5_MERGES.forEach((rango) => hoja.mergeCells(rango));

    // Fila 12 (encabezados): 2 líneas de alto para que "MONEDA NACIONAL/EXTRANJERA" no se corten.
    hoja.getRow(12).height = 40;

    // Firmas como imagen (recorte real de la plantilla oficial), en vez de las líneas de
    // texto. Se deja comentado el modo anterior por si se necesita volver a él.
    // this.agregarSeccionFirmas(hoja, 12, firmas);
    await this.insertarFirmasImagen(libroExcel, hoja, 12, '/assets/img/firma-anexos.png');

    hoja.pageSetup.printArea = `A1:L${hoja.rowCount}`;

    this.printExcel(nombreArchivo, libroExcel);
  }

  private aplicarEstiloA5(cell: ExcelJS.Cell, e: EstiloA5 | undefined): void {
    if (!e) return;

    cell.font = {
      name: e.font.name,
      size: e.font.size,
      bold: e.font.bold,
      ...(e.font.color ? { color: { argb: e.font.color } } : {}),
    };

    if (e.fill) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: e.fill } };
    }

    if (e.border) {
      const bd: Partial<ExcelJS.Borders> = {};
      if (e.border.t) bd.top = { style: e.border.t as ExcelJS.BorderStyle };
      if (e.border.b) bd.bottom = { style: e.border.b as ExcelJS.BorderStyle };
      if (e.border.l) bd.left = { style: e.border.l as ExcelJS.BorderStyle };
      if (e.border.r) bd.right = { style: e.border.r as ExcelJS.BorderStyle };
      cell.border = bd;
    }

    if (e.numFmt) cell.numFmt = e.numFmt;

    if (e.align) {
      cell.alignment = {
        ...(e.align.h ? { horizontal: e.align.h as ExcelJS.Alignment['horizontal'] } : {}),
        ...(e.align.v ? { vertical: e.align.v as ExcelJS.Alignment['vertical'] } : {}),
        ...(e.align.wrap ? { wrapText: true } : {}),
        // shrinkToFit sí es compatible con numFmt en Excel, solo hay conflicto con wrapText.
        ...(!e.align.wrap ? { shrinkToFit: true } : {}),
      };
    }
  }

  /* ===== ANEXO 6 — ESTADO DE EXCEDENTES O PÉRDIDAS CONDENSADO ===== */

  public async generateAnexo6(config: ConfigAnexo6): Promise<void> {
    const { titulo, subtitulo, cooperativa, fechaInformacion, valores, nombreArchivo, imagePath, imagePath2, textos } = config;

    const nombreHoja = 'Anexo6_ER';
    const libroExcel = this.createExcel(nombreHoja);
    const hoja = libroExcel.getWorksheet(nombreHoja);
    if (!hoja) {
      throw new Error(this.translate.instant('reportes-normativos.error-hoja-anexo6'));
    }

    const numColumnas = 6;

    // Abre el archivo en vista previa de salto de página, para ver de una vez cuántas hojas ocupa.
    hoja.views = [{ showGridLines: false, style: 'pageBreakPreview' }];
    hoja.properties.defaultRowHeight = 18;
    hoja.pageSetup = {
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      showGridLines: false,
      margins: { left: 0.236, right: 0.236, top: 0.748, bottom: 0.748, header: 0, footer: 0 },
    };

    Object.entries(ANEXO6_COLS).forEach(([col, ancho]) => {
      hoja.getColumn(col).width = ancho;
    });

    hoja.addRow([]);

    const row2 = hoja.addRow([titulo]);
    hoja.mergeCells('A2:F2');
    const cell2 = row2.getCell(1);
    cell2.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    cell2.font = this.FUENTE_BASE_BOLD;
    cell2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7E6E6' } };
    this.aplicarBordeFila(hoja, 2, numColumnas);

    hoja.addRow([]);

    const row4 = hoja.addRow([subtitulo]);
    hoja.mergeCells('A4:F4');
    const cell4 = row4.getCell(1);
    cell4.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    cell4.font = this.FUENTE_BASE_BOLD;
    this.aplicarBordeFila(hoja, 4, numColumnas);

    hoja.addRow([]);
    hoja.addRow([]);

    const row7 = hoja.addRow([textos.cooperativaLabel]);
    row7.getCell(1).font = this.FUENTE_BASE_BOLD;
    row7.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    hoja.mergeCells(row7.number, 1, row7.number, 3);
    const celdaCoop = row7.getCell(4);
    celdaCoop.value = cooperativa;
    celdaCoop.font = this.FUENTE_BASE;
    celdaCoop.alignment = { horizontal: 'left', vertical: 'middle' };
    celdaCoop.border = {
      top: { style: 'thin', color: this.COLOR_BORDE },
      bottom: { style: 'thin', color: this.COLOR_BORDE },
    };
    hoja.mergeCells(row7.number, 4, row7.number, numColumnas);

    hoja.addRow([]);
    hoja.addRow([]);

    const row10 = hoja.addRow([textos.fechaInformacionLabel]);
    row10.getCell(1).font = this.FUENTE_BASE_BOLD;
    row10.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    hoja.mergeCells(row10.number, 1, row10.number, 3);
    const celdaFecha = row10.getCell(4);
    celdaFecha.value = this.parseFecha(fechaInformacion);
    celdaFecha.numFmt = 'dd/mmmm/yyyy';
    celdaFecha.font = this.FUENTE_BASE;
    celdaFecha.alignment = { horizontal: 'left', vertical: 'middle' };
    celdaFecha.border = {
      top: { style: 'thin', color: this.COLOR_BORDE },
      bottom: { style: 'thin', color: this.COLOR_BORDE },
    };
    hoja.mergeCells(row10.number, 4, row10.number, numColumnas);

    const row11 = hoja.addRow([textos.cifrasLempiras]);
    row11.getCell(1).font = this.FUENTE_BASE;
    row11.getCell(1).alignment = { shrinkToFit: true };

    const headerRow = hoja.addRow([
      '', textos.colCta, textos.colTotal, textos.colMonedaNacional, textos.colCta, textos.colMonedaExtranjera,
    ]);
    for (let colNum = 1; colNum <= numColumnas; colNum++) {
      const cell = headerRow.getCell(colNum);
      // No se usa FUENTE_BASE_BOLD: fija color de tema y algunos visores lo pintan invisible sobre el gris.
      cell.font = { name: 'Calibri', size: 14, bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      // Barra gris para que se distinga como encabezado de columnas.
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFAEABAB' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
    }
    headerRow.height = 40;

    // Cuerpo de la tabla (filas 13-84), igual a la hoja oficial, con sus 3 subtotales por fórmula.
    for (const celda of ANEXO6_CELDAS) {
      const cell = hoja.getCell(celda.ref);

      if (celda.k === 'f') {
        cell.value = { formula: celda.f || '' } as any;
      } else if (celda.k === 'lk') {
        const codigo = celda.code ?? '';
        cell.value = codigo ? (valores[codigo] ?? 0) : '';
      } else {
        cell.value = celda.v ?? '';
      }

      this.aplicarEstiloA6(cell, ANEXO6_ESTILOS[celda.s]);
    }

    // Firmas como imagen (recorte real de la plantilla oficial), en vez de las líneas de
    // texto. Se deja comentado el modo anterior por si se necesita volver a él.
    // this.agregarSeccionFirmas(hoja, numColumnas, textos.firmas);
    await this.insertarFirmasImagen(libroExcel, hoja, numColumnas, '/assets/img/firma-anexos.png');

    // El logo se agrega al final a propósito: insertarlo antes corría una fila hacia
    // abajo todo el bloque institucional (ExcelJS actualiza rowCount al anclar la imagen).
    await this.insertarLogo(libroExcel, hoja, imagePath, imagePath2, { col: 0, row: 4.5 }, { width: 300, height: 64 });

    hoja.pageSetup.printArea = `A1:F${hoja.rowCount}`;

    this.printExcel(nombreArchivo, libroExcel);
  }

  private aplicarEstiloA6(cell: ExcelJS.Cell, e: EstiloA6 | undefined): void {
    if (!e) return;

    cell.font = {
      name: e.font.name,
      size: e.font.size,
      bold: e.font.bold,
      ...(e.font.color ? { color: { argb: e.font.color } } : {}),
    };

    if (e.fill) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: e.fill } };
    }

    if (e.border) {
      const bd: Partial<ExcelJS.Borders> = {};
      if (e.border.t) bd.top = { style: e.border.t as ExcelJS.BorderStyle };
      if (e.border.b) bd.bottom = { style: e.border.b as ExcelJS.BorderStyle };
      if (e.border.l) bd.left = { style: e.border.l as ExcelJS.BorderStyle };
      if (e.border.r) bd.right = { style: e.border.r as ExcelJS.BorderStyle };
      cell.border = bd;
    }

    if (e.numFmt) cell.numFmt = e.numFmt;

    if (e.align) {
      cell.alignment = {
        ...(e.align.h ? { horizontal: e.align.h as ExcelJS.Alignment['horizontal'] } : {}),
        ...(e.align.v ? { vertical: e.align.v as ExcelJS.Alignment['vertical'] } : {}),
        ...(e.align.wrap ? { wrapText: true } : {}),
        ...(e.align.indent ? { indent: e.align.indent } : {}),
        // Mismo ajuste que en aplicarEstiloA5: shrinkToFit solo es incompatible con wrapText.
        ...(!e.align.wrap ? { shrinkToFit: true } : {}),
      };
    }
  }

  /* ===== INDICADORES GLOBALES ===== */

  public async generateIndicadoresGlobales(config: ConfigIndicadoresGlobales): Promise<void> {
    const { cooperativa, fechaInformacion, datos, nombreArchivo, imagePath, imagePath2, textos } = config;

    const nombreHoja = 'indicadores globales';
    const numColumnas = 5;

    const libroExcel = this.createExcel(nombreHoja);
    const hoja = libroExcel.getWorksheet(nombreHoja);
    if (!hoja) {
      throw new Error(this.translate.instant('reportes-normativos.error-hoja-globales'));
    }

    // Abre el archivo en vista previa de salto de página, para ver de una vez cuántas hojas ocupa.
    hoja.views = [{ showGridLines: false, style: 'pageBreakPreview' }];
    hoja.properties.defaultRowHeight = 18;
    hoja.pageSetup = {
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      showGridLines: false,
      margins: { left: 0.236, right: 0.236, top: 0.748, bottom: 0.748, header: 0, footer: 0 },
    };

    hoja.columns = [
      { width: 60.2 }, { width: 37 }, { width: 17.6 }, { width: 11.6 }, { width: 17.6 },
    ];

    const row1 = hoja.addRow([textos.consejoNacional]);
    const row2 = hoja.addRow([textos.superintendencia]);
    const row3 = hoja.addRow([textos.tituloGlobales]);
    const row4 = hoja.addRow([(cooperativa || '').toUpperCase()]);
    const row5 = hoja.addRow([textos.cifrasAl + ' ' + this.fechaEnLetras(fechaInformacion)]);

    [row1, row2, row3, row4, row5].forEach((row, i) => {
      const fila = i + 1;
      hoja.mergeCells(fila, 1, fila, numColumnas);
      const cell = row.getCell(1);
      cell.font = this.FUENTE_BASE_BOLD;
      cell.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    });

    const altoLogoPx = await this.insertarLogosIndicadoresGlobales(libroExcel, hoja, imagePath, imagePath2);

    // Si el logo es más alto que una fila normal se encima con el encabezado, así que
    // reservamos aquí el alto real que necesita (px -> puntos, 1pt = 96/72 px).
    const filaBlancoLogo = hoja.addRow([]);
    if (altoLogoPx > 0) {
      const altoNecesarioPt = (altoLogoPx + 8) / (96 / 72);
      filaBlancoLogo.height = Math.max(hoja.properties.defaultRowHeight || 18, altoNecesarioPt);
    }

    const headerRow = hoja.addRow([
      textos.colIndicador, textos.colParametros, textos.colResultado, textos.colRango, textos.colPuntaje,
    ]);
    headerRow.eachCell((cell: ExcelJS.Cell, colNum: number) => {
      if (colNum <= numColumnas) {
        cell.font = this.FUENTE_BASE_BOLD;
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'medium' }, bottom: { style: 'medium' },
          left: { style: 'medium' }, right: { style: 'medium' },
        };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBFBFBF' } };
      }
    });

    for (const dato of datos) {
      if (dato.nivel === 1) {
        const textoSeccion = `${dato.numero} ${dato.descripcion}`.toUpperCase();
        const filaSeccion = hoja.addRow([textoSeccion]);
        hoja.mergeCells(filaSeccion.number, 1, filaSeccion.number, numColumnas);
        const cell = filaSeccion.getCell(1);
        cell.font = this.FUENTE_BASE_BOLD;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBFBFBF' } };
        this.aplicarBordeFila(hoja, filaSeccion.number, numColumnas);
        // Algunos visores solo leen el borde de la celda ancla del bloque fusionado.
        cell.border = {
          ...cell.border,
          right: { style: 'thin', color: this.COLOR_BORDE },
        };
        filaSeccion.height = this.calcularAlturaFila(textoSeccion, 60.2 + 37 + 17.6 + 11.6 + 17.6, 14);
        continue;
      }

      const textoDato = `${dato.numero} ${dato.descripcion}`;
      const filaDato = hoja.addRow([
        textoDato,
        dato.parametros ?? '',
        dato.resultado ?? '',
        dato.rango ?? '',
        dato.puntaje ?? '',
      ]);
      filaDato.eachCell((cell: ExcelJS.Cell, colNum: number) => {
        if (colNum <= numColumnas) {
          // PARÁMETROS mezcla números (porcentaje) y texto; solo los números van en % y negrita.
          const esParametroNumerico = colNum === 2 && typeof dato.parametros === 'number';
          const esNegrita = esParametroNumerico || colNum === 4 || colNum === 5; // RANGO y PUNTAJE van en negrita
          cell.font = esNegrita ? this.FUENTE_BASE_BOLD : this.FUENTE_BASE;
          cell.alignment = colNum === 1
            ? { horizontal: 'left', vertical: 'middle', wrapText: true }
            : { horizontal: 'center', vertical: 'middle', wrapText: true };
          if (esParametroNumerico) {
            cell.numFmt = '0.00%';
          }
          if (colNum === 5 && typeof dato.puntaje === 'number') {
            cell.numFmt = '0.00';
          }
          if (colNum === 4 && dato.rango && COLOR_RANGO_INDICADORES_GLOBALES[dato.rango]) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: COLOR_RANGO_INDICADORES_GLOBALES[dato.rango] },
            };
          }
          // Borde propio arriba/izq/der; el de abajo lo aporta el top de la fila siguiente.
          cell.border = {
            top: { style: 'thin', color: this.COLOR_BORDE },
            left: { style: 'thin', color: this.COLOR_BORDE },
            right: { style: 'thin', color: this.COLOR_BORDE },
          };
        }
      });
      filaDato.height = this.calcularAlturaFila(textoDato, 60.2, 14);
    }

    // SUMATORIA = puntajes obtenidos / 100 (los máximos ya suman 100). CALIFICACIÓN = SUMATORIA * 0.7.
    // Mismo cálculo que ind_financieros!W3:W5 del archivo de referencia.
    const totalPuntajeObtenido = datos
      .filter((d) => d.nivel === 2)
      .reduce((suma, d) => suma + (typeof d.puntaje === 'number' ? d.puntaje : 0), 0);
    const sumatoriaFraccion = totalPuntajeObtenido / 100;
    const calificacionFraccion = sumatoriaFraccion * 0.7;
    const calificacionPct = calificacionFraccion * 100;
    let nivelRiesgoTexto: string;
    if (calificacionPct >= 63) nivelRiesgoTexto = 'BAJO';
    else if (calificacionPct >= 56) nivelRiesgoTexto = 'MEDIO BAJO';
    else if (calificacionPct >= 49) nivelRiesgoTexto = 'MEDIO';
    else if (calificacionPct >= 42) nivelRiesgoTexto = 'MEDIO ALTO';
    else nivelRiesgoTexto = 'ALTO';

    const filaSumatoria = hoja.addRow([textos.sumatoriaBase, '', '', '', sumatoriaFraccion]);
    const filaCalificacion = hoja.addRow([textos.calificacionBase, '', '', '', calificacionFraccion]);
    const filaNivelRiesgo = hoja.addRow([textos.nivelRiesgoLabel, '', '', '', nivelRiesgoTexto]);
    [filaSumatoria, filaCalificacion, filaNivelRiesgo].forEach((row) => {
      row.getCell(1).font = this.FUENTE_BASE_BOLD;
      row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
      row.getCell(numColumnas).font = this.FUENTE_BASE_BOLD;
      row.getCell(numColumnas).alignment = { horizontal: 'center', vertical: 'middle' };
      this.aplicarBordeFila(hoja, row.number, numColumnas);
    });
    filaSumatoria.getCell(numColumnas).numFmt = '0.00%';
    filaCalificacion.getCell(numColumnas).numFmt = '0.00%';
    if (COLOR_NIVEL_RIESGO[nivelRiesgoTexto]) {
      filaNivelRiesgo.getCell(numColumnas).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COLOR_NIVEL_RIESGO[nivelRiesgoTexto] },
      };
    }

    hoja.addRow([]);
    hoja.addRow([]);

    // Esta hoja solo lleva 2 leyendas (no las 4 firmas de los otros anexos), mismo
    // criterio de línea con guion bajo que agregarSeccionFirmas.
    const filaLineaFirmas = hoja.addRow([]);
    filaLineaFirmas.height = 22;
    const bloquesFirmas: { c1: number; c2: number; texto: string }[] = [
      { c1: 1, c2: 1, texto: textos.elaboradoPor },
      { c1: 3, c2: numColumnas, texto: textos.autorizadoGerente },
    ];
    bloquesFirmas.forEach(({ c1, c2, texto }) => {
      let anchoBloque = 0;
      for (let c = c1; c <= c2; c++) anchoBloque += hoja.getColumn(c).width || 10;
      const deseadoPorTexto = Math.max(10, Math.round(texto.length * 1.5));
      const maxPorAncho = Math.max(5, Math.floor(anchoBloque * 0.9));
      const cantidadGuiones = Math.min(deseadoPorTexto, maxPorAncho);
      if (c2 > c1) hoja.mergeCells(filaLineaFirmas.number, c1, filaLineaFirmas.number, c2);
      const celdaLinea = hoja.getCell(filaLineaFirmas.number, c1);
      celdaLinea.value = '_'.repeat(cantidadGuiones);
      celdaLinea.font = this.FUENTE_BASE;
      celdaLinea.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    const filaFirmas = hoja.addRow([textos.elaboradoPor, '', textos.autorizadoGerente, '', '']);
    hoja.mergeCells(filaFirmas.number, 3, filaFirmas.number, numColumnas);
    filaFirmas.getCell(1).font = this.FUENTE_BASE_BOLD;
    filaFirmas.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    filaFirmas.getCell(3).font = this.FUENTE_BASE_BOLD;
    filaFirmas.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };

    hoja.pageSetup.printArea = `A1:E${hoja.rowCount}`;

    this.printExcel(nombreArchivo, libroExcel);
  }

  /* ===== ANEXO A — ACTIVOS PONDERADOS POR RIESGOS ===== */

  public async generateActivosPonderados(config: ConfigActivosPonderados): Promise<void> {
    const {
      datos, resumenPonderados, totalResumenSuma, totalResumenPonderado,
      capitalInstitucional, nombreArchivo, imagePath, imagePath2, textos,
    } = config;

    const nombreHoja = 'AnexoA_Ponderados';
    const libroExcel = this.createExcel(nombreHoja);
    const hoja = libroExcel.getWorksheet(nombreHoja);
    if (!hoja) {
      throw new Error(this.translate.instant('reportes-normativos.error-hoja-activos-ponderados'));
    }

    // A-D: tabla principal y bloque "resumen" (número/descripción/suma/ponderado).
    // E: separador. F-G: bloque "Capital Institucional Ajustado", al lado del resumen.
    const numColumnas = 7;

    // Abre el archivo en vista previa de salto de página, para ver de una vez cuántas hojas ocupa.
    hoja.views = [{ showGridLines: false, style: 'pageBreakPreview' }];
    hoja.properties.defaultRowHeight = 15;
    hoja.pageSetup = {
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      showGridLines: false,
      margins: { left: 0.236, right: 0.236, top: 0.748, bottom: 0.748, header: 0, footer: 0 },
    };

    // Mismos anchos de columna que la hoja oficial (Anexo A. Ponderados por Riesgos).
    hoja.columns = [
      { width: 20.29 }, { width: 68 }, { width: 19.57 }, { width: 23 },
      { width: 11.43 }, { width: 42.57 }, { width: 28.14 },
    ];

    hoja.addRow([]);

    const anchoTabla = 3;
    const row2 = hoja.addRow([textos.titulo]);
    hoja.mergeCells(row2.number, 1, row2.number, anchoTabla);
    const cell2 = row2.getCell(1);
    cell2.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: true };
    cell2.font = this.FUENTE_BASE_BOLD;

    // Los logos van al lado del título (columnas D-F), no abajo: solo 1 fila en
    // blanco antes del primer encabezado de sección.
    hoja.addRow([]);

    let primeraSeccion = true;
    for (const dato of datos) {
      if (dato.tipo === 'titulo') {
        // Una fila en blanco entre el final de una tabla y el encabezado de la siguiente.
        if (!primeraSeccion) hoja.addRow([]);
        primeraSeccion = false;

        const filaTitulo = hoja.addRow([dato.descripcion]);
        hoja.mergeCells(filaTitulo.number, 1, filaTitulo.number, anchoTabla);
        this.aplicarBannerSeccion(hoja, filaTitulo.number, anchoTabla);
        continue;
      }

      if (dato.tipo === 'seccion') {
        // Subencabezado dentro de la sección (ej. "Total activo BG"): mismo layout de
        // 3 columnas (código/descripción/valor) que cualquier cuenta, sin fusionar
        // celdas; código y descripción van resaltados (gris, negrita, centrado), el
        // valor va normal (sin relleno), igual que en la hoja oficial.
        const filaSeccion = hoja.addRow([dato.codigo, dato.descripcion, dato.valor]);
        filaSeccion.eachCell((cell: ExcelJS.Cell, colNum: number) => {
          if (colNum > anchoTabla) return;
          cell.border = {
            top: { style: 'thin', color: this.COLOR_BORDE },
            bottom: { style: 'thin', color: this.COLOR_BORDE },
            left: { style: 'thin', color: this.COLOR_BORDE },
            right: { style: 'thin', color: this.COLOR_BORDE },
          };
          if (colNum === 3) {
            cell.font = this.FUENTE_A4;
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            cell.numFmt = '#,##0.00';
          } else {
            cell.font = this.FUENTE_A4_BOLD;
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } };
          }
        });
        continue;
      }

      // El total general lleva una fila en blanco antes, como en la hoja oficial.
      if (dato.tipo === 'total') hoja.addRow([]);

      // Las filas sin código (Ajuste.../Subtotal.../Total...) combinan A+B: el texto
      // no es una cuenta, ocupa el espacio de código y descripción juntos. Al combinar,
      // el texto tiene que ir en la celda A (la que queda "viva" tras el merge).
      const sinCodigo = !dato.codigo;
      const fila = hoja.addRow([
        sinCodigo ? dato.descripcion : dato.codigo,
        sinCodigo ? '' : dato.descripcion,
        dato.valor,
      ]);
      if (sinCodigo) hoja.mergeCells(fila.number, 1, fila.number, 2);
      // La etiqueta (Ajuste.../Subtotal.../Total...) va en negrita siempre que no tenga
      // código; el valor solo en subtotales y en el total general, no en los ajustes.
      // dato.codigoBold es aparte: negrita solo en la columna de código (ej. "1 Resto de
      // Activos"), con descripción y valor normales.
      const fuenteCodigo = (sinCodigo || dato.codigoBold) ? this.FUENTE_A4_BOLD : this.FUENTE_A4;
      const fuenteDescripcion = sinCodigo ? this.FUENTE_A4_BOLD : this.FUENTE_A4;
      const fuenteValor = (dato.tipo === 'destacado' || dato.tipo === 'total') ? this.FUENTE_A4_BOLD : this.FUENTE_A4;
      fila.eachCell((cell: ExcelJS.Cell, colNum: number) => {
        if (colNum > anchoTabla) return;
        cell.font = colNum === 3 ? fuenteValor : (colNum === 1 ? fuenteCodigo : fuenteDescripcion);
        // A diferencia de aplicarBordeFila (solo el borde exterior), acá cada celda
        // lleva los 4 lados: son columnas independientes (código/descripción/valor),
        // no una sola celda combinada como en las filas de encabezado de sección.
        cell.border = {
          top: { style: 'thin', color: this.COLOR_BORDE },
          bottom: { style: 'thin', color: this.COLOR_BORDE },
          left: { style: 'thin', color: this.COLOR_BORDE },
          right: { style: 'thin', color: this.COLOR_BORDE },
        };
        if (colNum === 1) {
          cell.alignment = (sinCodigo || dato.codigoBold)
            ? { horizontal: 'left', vertical: 'middle', shrinkToFit: true }
            : { horizontal: 'center', vertical: 'middle' };
        } else if (colNum === 2) {
          cell.alignment = { horizontal: 'left', vertical: 'middle', shrinkToFit: true };
        } else {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0.00';
        }
      });
    }

    // 2 filas en blanco antes de este bloque, como en la hoja oficial.
    hoja.addRow([]);
    hoja.addRow([]);
    this.agregarResumenActivosPonderados(hoja, resumenPonderados, totalResumenSuma, totalResumenPonderado, capitalInstitucional, textos);

    this.agregarSeccionFirmas(hoja, numColumnas, [textos.elaboradoPor, textos.autorizadoPor]);

    // El logo va al final, mismo motivo que en el Anexo 6: insertarlo antes corre
    // hacia abajo las filas ya escritas. Va al lado del título (columna D en
    // adelante), no debajo, por eso no reserva filas propias.
    await this.insertarLogo(libroExcel, hoja, imagePath, imagePath2, { col: 3.4, row: 0.5 }, { width: 300, height: 50 });

    hoja.pageSetup.printArea = `A1:${hoja.getColumn(numColumnas).letter}${hoja.rowCount}`;

    this.printExcel(nombreArchivo, libroExcel);
  }

  /** Bloque final del Anexo A: "Total de Activos Ponderados por Riesgos" (número,
   * descripción, suma y ponderado por tramo) al lado de "Capital Institucional
   * Ajustado", igual que en la hoja oficial (no va todo en una sola columna). */
  private agregarResumenActivosPonderados(
    hoja: ExcelJS.Worksheet,
    resumenPonderados: FilaResumenActivoPonderado[],
    totalSuma: number,
    totalPonderado: number,
    capitalInstitucional: FilaCapitalInstitucional[],
    textos: TextosActivosPonderados,
  ): void {
    const filaHeader = hoja.addRow([]);
    hoja.mergeCells(filaHeader.number, 1, filaHeader.number, 2);
    hoja.mergeCells(filaHeader.number, 6, filaHeader.number, 7);
    filaHeader.getCell(1).value = textos.resumenTitulo;
    filaHeader.getCell(3).value = textos.colSumaActivos;
    filaHeader.getCell(4).value = textos.colActivosPonderados;
    filaHeader.getCell(6).value = textos.capitalInstitucionalTitulo;
    // El relleno y el borde van en las 2 celdas de cada rango combinado (1-2 y 6-7),
    // no solo en la maestra: mismo motivo que aplicarBannerSeccion.
    [1, 2, 3, 4, 6, 7].forEach((c) => {
      const cell = filaHeader.getCell(c);
      cell.font = this.FUENTE_A4_BOLD;
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } };
      cell.border = {
        top: { style: 'thin', color: this.COLOR_BORDE },
        bottom: { style: 'thin', color: this.COLOR_BORDE },
        left: { style: 'thin', color: this.COLOR_BORDE },
        right: { style: 'thin', color: this.COLOR_BORDE },
      };
    });

    // El bloque de capital va en 2 cajas separadas por una fila en blanco, como en la
    // hoja oficial: la primera termina en "Total capital institucional ajustado".
    const capitalConHueco: (FilaCapitalInstitucional | null)[] = [];
    capitalInstitucional.forEach((fila) => {
      capitalConHueco.push(fila);
      if (/^total/i.test(fila.descripcion.trim())) capitalConHueco.push(null);
    });

    const filasCapital = [
      ...capitalConHueco,
      ...Array(Math.max(0, resumenPonderados.length + 1 - capitalConHueco.length)).fill(null),
    ];
    const totalFilas = Math.max(resumenPonderados.length + 1, capitalConHueco.length);

    for (let i = 0; i < totalFilas; i++) {
      const fila = hoja.addRow([]);
      const esUltimaResumen = i === resumenPonderados.length;
      const r = i < resumenPonderados.length ? resumenPonderados[i] : (esUltimaResumen ? { numero: null, descripcion: textos.totalesLabel, suma: totalSuma, ponderado: totalPonderado } : null);

      if (r) {
        fila.getCell(1).value = r.numero ?? '';
        fila.getCell(2).value = r.descripcion;
        fila.getCell(3).value = r.suma;
        fila.getCell(4).value = r.ponderado;
        // Toda la tabla va en negrita, no solo el total, a pedido del revisor.
        [1, 2, 3, 4].forEach((c) => {
          const cell = fila.getCell(c);
          cell.font = this.FUENTE_A4_BOLD;
          // Bordes completos (no solo el de abajo): son columnas independientes de
          // una tabla con cuadrícula, igual que las tablas de tramos de riesgo.
          cell.border = {
            top: { style: 'thin', color: this.COLOR_BORDE },
            bottom: { style: 'thin', color: this.COLOR_BORDE },
            left: { style: 'thin', color: this.COLOR_BORDE },
            right: { style: 'thin', color: this.COLOR_BORDE },
          };
          cell.alignment = c === 2
            ? { horizontal: 'left', vertical: 'middle', wrapText: true }
            : { horizontal: c === 1 ? 'center' : 'right', vertical: 'middle' };
          if (c === 3 || c === 4) cell.numFmt = '#,##0.00';
        });
      }

      const c = filasCapital[i];
      if (c) {
        fila.getCell(6).value = c.descripcion;
        fila.getCell(7).value = c.valor;
        // Toda la tabla va en negrita, no solo el total, a pedido del revisor.
        [6, 7].forEach((col) => {
          const cell = fila.getCell(col);
          cell.font = this.FUENTE_A4_BOLD;
          cell.border = {
            top: { style: 'thin', color: this.COLOR_BORDE },
            bottom: { style: 'thin', color: this.COLOR_BORDE },
            left: { style: 'thin', color: this.COLOR_BORDE },
            right: { style: 'thin', color: this.COLOR_BORDE },
          };
        });
        fila.getCell(6).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        fila.getCell(7).alignment = { horizontal: 'right', vertical: 'middle' };
        fila.getCell(7).numFmt = c.porcentaje ? '0.00%' : '#,##0.00';
      }
    }
  }

  /** "31 DE OCTUBRE DE 2025" a partir de una fecha dd/mm/yyyy. */
  public fechaEnLetras(fecha: string): string {
    const d = this.parseFecha(fecha);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${d.getUTCDate()} DE ${CommonVariables.f_switchmes(mm)} DE ${d.getUTCFullYear()}`;
  }
}
